import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Wind } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-full bg-navy flex items-center justify-center"><Wind size={20} className="text-gold" /></div>
          <div className="font-display font-bold text-navy text-xl">Breathe<span className="text-gold">·</span>Thrive</div>
        </Link>
        <div className="bg-warm-white border border-edge rounded-3xl p-8 shadow-[0_8px_30px_rgba(44,45,91,0.06)]">
          <h1 className="font-display text-2xl font-bold text-navy">Forgot your password?</h1>
          <p className="text-sm text-ink-mid mt-1">Enter your account email and we'll send you a reset link.</p>
          {sent ? (
            <div data-testid="forgot-sent" className="mt-6 space-y-3">
              <div className="text-sm bg-gold/10 border border-gold/40 rounded-xl px-4 py-3 text-ink-dark">
                <div className="font-display text-navy font-semibold mb-1">Check your email</div>
                <div>If an account exists for <strong>{email}</strong>, a reset link is on its way. It's good for the next 60 minutes.</div>
                <div className="text-xs text-ink-soft mt-2">Didn't receive it? Check your spam folder, or wait a minute and try again.</div>
              </div>
              <Link to="/login" data-testid="forgot-back-to-login" className="block text-center text-sm text-navy font-semibold hover:underline">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Email</label>
                <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-4 py-3 rounded-xl border border-edge bg-cream focus:outline-none focus:ring-2 focus:ring-gold text-ink-dark" />
              </div>
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="forgot-error">{err}</div>}
              <button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full bg-navy text-warm-white px-5 py-3 rounded-full font-semibold hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <Link to="/login" data-testid="forgot-back-link" className="block text-center text-sm text-ink-soft hover:text-navy">← Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
